import React, { useMemo } from 'react';
import {
  computeQuicklogBarScale,
  normalizeQuicklogIntervals,
  plainObjectToListVisualMaps,
  resolveListVisual,
} from '../../../utils/drillz/listValueVisuals';
import { formatReportDepth } from '../../../utils/dailyReport/drillingInProgressForReport';

const DEFAULT_TRACK_HEIGHT = 22;

function buildDepthTicks(scaleMax) {
  const step = scaleMax <= 200 ? 50 : scaleMax <= 600 ? 100 : 200;
  const ticks = [];
  for (let d = 0; d <= scaleMax; d += step) {
    ticks.push(d);
  }
  if (ticks[ticks.length - 1] !== scaleMax) {
    ticks.push(Math.round(scaleMax));
  }
  return ticks;
}

const QuicklogHoleBar = ({
  quicklogRows = [],
  plannedDepth,
  listVisualMaps = null,
  variant = 'screen',
  trackHeightPx = DEFAULT_TRACK_HEIGHT,
}) => {
  const intervals = useMemo(() => normalizeQuicklogIntervals(quicklogRows), [quicklogRows]);
  const maps = useMemo(
    () =>
      listVisualMaps instanceof Map
        ? listVisualMaps
        : plainObjectToListVisualMaps(listVisualMaps),
    [listVisualMaps]
  );
  const rockMap = maps.get('rock_type');

  const { deepestTo, plannedNum, scaleMax } = useMemo(
    () => computeQuicklogBarScale(intervals, plannedDepth),
    [intervals, plannedDepth]
  );

  const ticks = useMemo(() => buildDepthTicks(scaleMax), [scaleMax]);

  const isPrint = variant === 'print';
  const labelColor = isPrint ? '#6b7280' : '#9ca3af';
  const trackBg = isPrint ? '#e5e7eb' : '#374151';
  const unloggedBg = isPrint ? '#f3f4f6' : '#1f2937';

  if (!intervals.length) {
    return null;
  }

  return (
    <div
      style={{
        padding: isPrint ? '4px 6px 6px' : '8px 10px 10px',
        borderBottom: isPrint ? '1px solid #e5e7eb' : '1px solid #374151',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: isPrint ? '7px' : '11px',
          color: labelColor,
          marginBottom: '4px',
          gap: '8px',
        }}
      >
        <span>Deepest: {formatReportDepth(deepestTo)} m</span>
        {plannedNum > 0 ? (
          <span>Planned: {formatReportDepth(plannedNum)} m</span>
        ) : null}
      </div>

      <div
        style={{
          position: 'relative',
          height: `${trackHeightPx}px`,
          borderRadius: '3px',
          overflow: 'hidden',
          background: trackBg,
          border: isPrint ? '1px solid #d1d5db' : '1px solid #4b5563',
        }}
      >
        {intervals.map((row, index) => {
          const leftPct = (row.from_depth / scaleMax) * 100;
          const widthPct = ((row.to_depth - row.from_depth) / scaleMax) * 100;
          const visual = resolveListVisual(rockMap, row.rock_type);
          const bg = visual.displayColor || '#9ca3af';
          const textureUrl = visual.texture?.dataUrl;

          return (
            <div
              key={`${row.from_depth}-${row.to_depth}-${index}`}
              title={`${row.rock_type || ''} ${row.from_depth}–${row.to_depth} m`}
              style={{
                position: 'absolute',
                left: `${leftPct}%`,
                width: `${Math.max(widthPct, 0.15)}%`,
                top: 0,
                bottom: 0,
                backgroundColor: bg,
                backgroundImage: textureUrl ? `url(${textureUrl})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                boxSizing: 'border-box',
                borderRight: '1px solid rgba(0,0,0,0.12)',
              }}
            />
          );
        })}

        {plannedNum > deepestTo + 0.01 ? (
          <div
            style={{
              position: 'absolute',
              left: `${(deepestTo / scaleMax) * 100}%`,
              width: `${((plannedNum - deepestTo) / scaleMax) * 100}%`,
              top: 0,
              bottom: 0,
              background: unloggedBg,
              opacity: 0.85,
            }}
          />
        ) : null}
      </div>

      <div
        style={{
          position: 'relative',
          height: '14px',
          marginTop: '2px',
          fontSize: isPrint ? '6.5px' : '10px',
          color: labelColor,
        }}
      >
        {ticks.map((tick) => (
          <span
            key={tick}
            style={{
              position: 'absolute',
              left: `${(tick / scaleMax) * 100}%`,
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap',
            }}
          >
            {tick}
          </span>
        ))}
      </div>
    </div>
  );
};

export default QuicklogHoleBar;
